import { FieldArray, FieldArrayRenderProps, useField, useFormikContext } from "formik";
import { BulkTicketTransferProps } from "../../types";
import React from "react";
import ErrorField from "shared/form/fields/errorField";
import { MaterialTableColumn } from "shared/components/materialTable/types";
import { ApiBulkTicket } from "apps/myExperience/modules/bulkTicketManagement/types";
import CheckBox from "shared/form/core/checkbox";
import { useColumnsForBulkTicketsTransferViewMode } from "../utils";
import { getBulkTicketsInfo } from "apps/myExperience/modules/bulkTicketManagement/utils";
import sortBy from "lodash/sortBy";
import intersection from "lodash/intersection";
import xor from "lodash/xor";
import MaterialTable from "shared/components/materialTable";
import Size from "shared/enums/Size";
import IndeterminateCheckbox from "shared/form/core/checkbox/indeterminateCheckbox";
import CheckboxStateEnum from "shared/enums/CheckboxStateEnum";

function getCheckboxState(values: Array<any>, allValues: Array<any>): CheckboxStateEnum {
  if (values.length === 0) {
    return CheckboxStateEnum.UNCHECKED;
  }
  if (values.length === allValues.length) {
    return CheckboxStateEnum.CHECKED;
  }
  return CheckboxStateEnum.INDETERMINATE;
}

export function BulkTicketPassBookFormField(
  props: BulkTicketTransferProps & { helpers?: FieldArrayRenderProps; values?: Array<string> }
) {
  const [field, meta] = useField("bulkTicketIds");
  return (
    <React.Fragment>
      <FieldArray name="bulkTicketIds">
        {helpers => {
          return <BulkTicketPassBookFormTable {...props} helpers={helpers} values={field.value} />;
        }}
      </FieldArray>
      <ErrorField meta={meta} />
    </React.Fragment>
  );
}

function BulkTicketPassBookFormTable(
  props: BulkTicketTransferProps & { helpers: FieldArrayRenderProps; values: Array<string> }
) {
  const [field] = useField("bulkTicketIds");
  const { setFieldValue } = useFormikContext();
  const commonColumns = useColumnsForBulkTicketsTransferViewMode();
  const info = React.useMemo(() => getBulkTicketsInfo(props.bulkTickets), [props.bulkTickets]);
  const columns: Array<MaterialTableColumn<ApiBulkTicket>> = React.useMemo(() => {
    return [
      {
        name: "",
        className: "blk_col_select",
        Cell: item => {
          return (
            <CheckBox
              size={Size.SMALL}
              checked={props?.values?.includes(item.id)}
              autoFocus={true}
              onChange={(e: any) => {
                if (e.target.checked) {
                  props?.helpers?.push(item.id);
                } else {
                  if (props?.values?.includes(item.id)) {
                    props.helpers.remove(props.values.indexOf(item.id));
                  }
                }
              }}
              label={""}
              name={""}
            />
          );
        }
      },
      ...commonColumns
    ];
  }, [commonColumns, props.helpers, props.values]);

  return (
    <React.Fragment>
      {sortBy(Object.keys(info.purchasesByFormType)).map(formType => {
        const purchases = sortBy(
          info.purchasesByFormType[formType].filter(item => item.valid),
          "purchasedOn"
        );
        const selectedPurchasesFromFormType = intersection(
          purchases.map(item => item.id),
          field.value
        );
        if (!purchases.length) {
          return null;
        }
        return (
          <div key={formType} className="blkTicketTransferModal_tableWrapper">
            <div className="blkTicketTransferModal_tableHeader">
              <IndeterminateCheckbox
                state={getCheckboxState(selectedPurchasesFromFormType, purchases)}
                label={formType}
                handleChange={checked => {
                  if (checked) {
                    info.mapByFormTypeToPurchaseIds[formType].forEach(id => {
                      props?.helpers?.push(id);
                    });
                  } else {
                    const common = intersection(info.mapByFormTypeToPurchaseIds[formType], field.value);
                    if (common.length > 0) {
                      setFieldValue("bulkTicketIds", xor(field.value, common));
                    }
                  }
                }}
              />
            </div>
            <MaterialTable columns={columns} data={purchases} />
          </div>
        );
      })}
    </React.Fragment>
  );
}
